import type { PlatformAiAgentSettings } from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import {
	getPlatformAiAgentSettings,
	publishPlatformAiAgentSettings,
	restorePlatformAiAgentSettings,
	updatePlatformAiAgentSettings,
} from '../api.js';

function publishedAt(value: string): string {
	return new Intl.DateTimeFormat(undefined, {
		dateStyle: 'medium',
		timeStyle: 'short',
	}).format(new Date(value));
}

export function PlatformAiAgents() {
	const [masterPrompt, setMasterPrompt] = useState('');
	const [settings, setSettings] = useState<PlatformAiAgentSettings>();
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();

	useEffect(() => {
		let current = true;
		void getPlatformAiAgentSettings()
			.then((loaded) => {
				if (current) {
					setMasterPrompt(loaded.masterPrompt);
					setSettings(loaded);
				}
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load the AI agent prompt',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, []);

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await updatePlatformAiAgentSettings({ masterPrompt });
			setMasterPrompt(updated.masterPrompt);
			setSettings(updated);
			setMessage('Draft saved. Publish it when it is ready for new conversations.');
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not save the AI agent prompt',
			);
		} finally {
			setBusy(false);
		}
	}

	async function publish(): Promise<void> {
		if (busy || !settings?.draftChanged || masterPrompt !== settings.masterPrompt) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await publishPlatformAiAgentSettings();
			setSettings(updated);
			setMasterPrompt(updated.masterPrompt);
			setMessage(`Published version ${updated.publishedVersion?.version ?? ''}.`);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not publish the Draft');
		} finally {
			setBusy(false);
		}
	}

	async function restore(versionId: string, version: number): Promise<void> {
		if (
			busy ||
			!window.confirm(`Restore previous version ${version} as a new Published version?`)
		) {
			return;
		}
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await restorePlatformAiAgentSettings(versionId);
			setSettings(updated);
			setMasterPrompt(updated.masterPrompt);
			setMessage(
				`Version ${version} restored as Published version ${updated.publishedVersion?.version}.`,
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not restore that version');
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="workspace-section owner-settings-page">
			<div className="section-heading">
				<div>
					<p className="eyebrow">Platform AI</p>
					<h2>AI Agents</h2>
					<p className="muted">
						This master prompt is used for every customer AI agent before its own
						persona and retrieved knowledge.
					</p>
				</div>
			</div>

			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}

			{loading ? (
				<div className="empty-configuration">Loading AI agent settings…</div>
			) : (
				<form className="owner-settings-form" onSubmit={(event) => void save(event)}>
					<section className="settings-card integration-panel ai-version-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Version and publish</h4>
							<p>
								Editing below changes the Draft only. New AI conversations use the
								Published version, while conversations already in progress remain
								pinned to the version they started with.
							</p>
						</div>
						<div className="ai-version-current">
							<div>
								<span>Draft</span>
								<strong>
									{settings?.draftChanged
										? 'Changes to publish'
										: 'Matches Published'}
								</strong>
							</div>
							<div>
								<span>Published</span>
								<strong>
									{settings?.publishedVersion
										? `Version ${settings.publishedVersion.version}`
										: 'Not published'}
								</strong>
								{settings?.publishedVersion && (
									<small>
										{settings.publishedVersion.publishedBy} ·{' '}
										{publishedAt(settings.publishedVersion.publishedAt)}
									</small>
								)}
							</div>
						</div>
						<button
							className="primary-button"
							disabled={
								busy ||
								!settings?.draftChanged ||
								masterPrompt !== settings.masterPrompt
							}
							onClick={() => void publish()}
							type="button"
						>
							Publish Draft
						</button>
						{!!settings?.previousVersions.length && (
							<details className="ai-version-history">
								<summary>
									Previous versions ({settings.previousVersions.length})
								</summary>
								<ul>
									{settings.previousVersions.map((version) => (
										<li key={version.id}>
											<div>
												<strong>Version {version.version}</strong>
												<span>
													{version.publishedBy} ·{' '}
													{publishedAt(version.publishedAt)}
												</span>
											</div>
											<button
												className="secondary-button"
												disabled={busy}
												onClick={() =>
													void restore(version.id, version.version)
												}
												type="button"
											>
												Restore
											</button>
										</li>
									))}
								</ul>
							</details>
						)}
					</section>
					<section className="settings-card integration-panel owner-ai-panel">
						<div className="integration-panel-heading">
							<h4 className="panel-title">Master prompt</h4>
							<p>
								Use <code>{'{{agent_name}}'}</code> wherever the agent's current
								name should appear. The current name is also supplied separately as
								protected context.
							</p>
						</div>
						<label className="owner-settings-field">
							<span>Instructions for all AI agents</span>
							<textarea
								maxLength={10_000}
								onChange={(event) => {
									setMasterPrompt(event.currentTarget.value);
									setMessage(undefined);
								}}
								required
								rows={18}
								value={masterPrompt}
							/>
						</label>
					</section>

					<button className="primary-button owner-settings-save" disabled={busy}>
						{busy ? 'Saving…' : 'Save Draft'}
					</button>
				</form>
			)}
		</section>
	);
}
